import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { InputSwitchModule } from 'primeng/inputswitch';
import { SliderModule } from 'primeng/slider';
import * as turf from '@turf/turf';
import { InputComponent } from '../input/input.component';
import { ButtonModule } from 'primeng/button';
import { InputSelectComponent } from '../input-select/input-select.component';
import { OrganizationService } from '../../model/organization/organization.service';
import { Organization } from '../../model/organization/organization.model';

@Component({
  selector: 'app-map-layer-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputSwitchModule,
    SliderModule,
    InputComponent,
    ButtonModule,
    InputSelectComponent,
  ],
  templateUrl: './map-layer-dialog.component.html',
  styleUrls: ['./map-layer-dialog.component.scss'],
})
export class MapLayerDialogComponent implements OnChanges, OnInit {
  @Input() error: string | null = null;
  @Input() selectedLayer: any;
  @Output() save = new EventEmitter<any>();
  @Output() close = new EventEmitter<void>();
  @Output() change = new EventEmitter<any>();

  form: FormGroup;
  squareMeters = 0;
  organizations: Organization[] = [];
  organizationsLoading = true;

  // Preset color options.
  colors = [
    '#5CE3CD',
    '#E85A57',
    '#D2E455',
    '#E35CC8',
    '#6F8CFF',
    '#FFFFFF',
    '#000000',
  ];

  constructor(
    private fb: FormBuilder,
    private organizationService: OrganizationService
  ) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      color: ['#5CE3CD', Validators.required],
      showSwimLanes: [false],
      gutterSpacing: [null, [Validators.min(0.01)]],
      lineWidth: [null, [Validators.min(0.01)]],
      rotationDegrees: [
        0,
        [Validators.required, Validators.min(0), Validators.max(360)],
      ],
      organizationId: [null, Validators.required],
    });

    // When showSwimLanes is true, require gutterSpacing and lineWidth.
    this.form.get('showSwimLanes')?.valueChanges.subscribe((show) => {
      const gutterControl = this.form.get('gutterSpacing');
      const lineWidthControl = this.form.get('lineWidth');
      if (show) {
        gutterControl?.setValidators([
          Validators.required,
          Validators.min(0.01),
        ]);
        lineWidthControl?.setValidators([
          Validators.required,
          Validators.min(0.01),
        ]);
      } else {
        gutterControl?.clearValidators();
        lineWidthControl?.clearValidators();
      }
      gutterControl?.updateValueAndValidity();
      lineWidthControl?.updateValueAndValidity();
    });

    // Subscribe to all form value changes to emit real-time updates
    this.form.valueChanges.subscribe((values) => {
      if (this.selectedLayer) {
        // Enforce minimums for gutterSpacing and lineWidth for broadcasting
        let gutterSpacing = values.gutterSpacing;
        let lineWidth = values.lineWidth;
        if (!gutterSpacing || gutterSpacing <= 0) {
          gutterSpacing = 5;
        }
        if (!lineWidth || lineWidth <= 0) {
          lineWidth = 1;
        }
        const previewLayer = {
          ...this.selectedLayer,
          name: values.name,
          description: values.description,
          organizationId: values.organizationId,
          jsonMetadata: {
            color: values.color,
            showSwimLanes: values.showSwimLanes,
            gutterSpacing,
            lineWidth,
            rotationDegrees: values.rotationDegrees,
          },
        };
        this.change.emit(previewLayer);
      }
    });
  }

  ngOnInit(): void {
    this.loadOrganizations();
  }

  async loadOrganizations(): Promise<void> {
    try {
      this.organizationsLoading = true;
      this.organizations = await this.organizationService.fetchAll();
    } catch (error) {
      console.error('Error loading organizations:', error);
    } finally {
      this.organizationsLoading = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedLayer'] && this.selectedLayer) {
      this.form.patchValue({
        name: this.selectedLayer.name || '',
        description: this.selectedLayer.description || '',
        color: this.selectedLayer.jsonMetadata?.color || '#5CE3CD',
        showSwimLanes: this.selectedLayer.jsonMetadata?.showSwimLanes ?? false,
        gutterSpacing: this.selectedLayer.jsonMetadata?.gutterSpacing ?? null,
        lineWidth: this.selectedLayer.jsonMetadata?.lineWidth ?? null,
        rotationDegrees: this.selectedLayer.jsonMetadata?.rotationDegrees ?? 0,
        organizationId: this.selectedLayer.organizationId || null,
      });
      if (
        this.selectedLayer.jsonContent &&
        this.selectedLayer.jsonContent.geometry
      ) {
        this.squareMeters = turf.area(this.selectedLayer.jsonContent);
      }
    }
  }

  onColorSelect(color: string): void {
    this.form.get('color')?.setValue(color);

    // Manually emit a change event after color selection
    // This ensures the map updates immediately when selecting a color swatch
    if (this.selectedLayer) {
      const previewLayer = {
        ...this.selectedLayer,
        name: this.form.value.name,
        description: this.form.value.description,
        organizationId: this.form.value.organizationId,
        jsonMetadata: {
          color: color,
          showSwimLanes: this.form.value.showSwimLanes,
          gutterSpacing: this.form.value.gutterSpacing,
          lineWidth: this.form.value.lineWidth,
          rotationDegrees: this.form.value.rotationDegrees,
        },
      };
      this.change.emit(previewLayer);
    }
  }

  onClose(): void {
    this.close.emit();
  }

  onSave(): void {
    // Mark all fields as touched to trigger validation messages
    this.form.markAllAsTouched();

    // Check if the form is valid before saving
    if (this.form.invalid) {
      return;
    }

    // Use defaults if gutterSpacing or lineWidth are missing or zero
    let gutterSpacing = this.form.value.gutterSpacing;
    let lineWidth = this.form.value.lineWidth;
    if (!gutterSpacing || gutterSpacing <= 0) {
      gutterSpacing = 5; // default value
    }
    if (!lineWidth || lineWidth <= 0) {
      lineWidth = 1; // default value
    }

    const updatedLayer = {
      ...this.selectedLayer,
      name: this.form.value.name,
      description: this.form.value.description,
      organizationId: this.form.value.organizationId,
      jsonMetadata: JSON.stringify({
        color: this.form.value.color,
        showSwimLanes: this.form.value.showSwimLanes,
        gutterSpacing,
        lineWidth,
        rotationDegrees: this.form.value.rotationDegrees,
      }),
      jsonContent: JSON.stringify(this.selectedLayer.jsonContent),
    };
    this.save.emit(updatedLayer);
  }
}
